import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  MessageCircle, 
  Twitter, 
  Facebook, 
  Instagram,
  ExternalLink,
  Copy,
  CheckCircle,
  AlertCircle,
  Music2,
  Key,
  Shield,
  FileText,
  ArrowRight,
  Link2,
  Settings2,
  Calendar,
  Zap,
  Play,
  BarChart3,
  Users,
  Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// How It Works Flow Component
const HowItWorksSection = () => {
  const steps = [
    {
      number: 1,
      title: "Connect Accounts",
      description: "Link your social media accounts (Telegram, Twitter, Facebook, etc.) using API keys",
      icon: Link2,
      color: "text-blue-400",
      bgColor: "bg-blue-500/20",
      details: ["Get API keys from each platform", "Enter credentials in Connected Accounts", "Verify connection is successful"]
    },
    {
      number: 2,
      title: "Create Channel",
      description: "Create a channel linking your account to a brand with selected content engines",
      icon: Settings2,
      color: "text-purple-400",
      bgColor: "bg-purple-500/20",
      details: ["Go to Schedule Manager", "Click 'Create Channel'", "Select platform & account", "Choose brand & engines"]
    },
    {
      number: 3,
      title: "Configure Schedule",
      description: "Set posting times for each engine on your channel",
      icon: Calendar,
      color: "text-green-400",
      bgColor: "bg-green-500/20",
      details: ["Expand channel card", "Click on each engine", "Set posting times (e.g., 10:00, 18:00)", "Save schedule"]
    },
    {
      number: 4,
      title: "Autopilot Runs",
      description: "SocialBlast automatically generates and posts content at scheduled times",
      icon: Zap,
      color: "text-yellow-400",
      bgColor: "bg-yellow-500/20",
      details: ["Content generated by AI", "Images fetched automatically", "Posts sent to your channels", "No manual work needed!"]
    },
    {
      number: 5,
      title: "Monitor & Adjust",
      description: "Track performance in Control Room and adjust as needed",
      icon: BarChart3,
      color: "text-pink-400",
      bgColor: "bg-pink-500/20",
      details: ["View post history", "Check success/failure rates", "Pause/resume channels", "Adjust schedules anytime"]
    }
  ];

  const engines = [
    { name: "Crypto Movers", emoji: "🪙", desc: "Trending coins with Harry Potter theme" },
    { name: "Crypto Signals", emoji: "📊", desc: "Trading signals and market analysis" },
    { name: "Sports Lifecycle", emoji: "⚽", desc: "Match updates and sports news" },
    { name: "Casino Trivia", emoji: "🎰", desc: "Fun casino facts and trivia" },
    { name: "Casino News", emoji: "🗞️", desc: "Gaming industry updates" },
    { name: "Meme Engagement", emoji: "😂", desc: "Fun crypto memes and hot takes" },
    { name: "Bollywood News", emoji: "🎬", desc: "Celebrity news and entertainment" },
    { name: "How to Play", emoji: "📚", desc: "Game guides and tutorials" },
  ];

  return (
    <div className="space-y-8">
      {/* Main Flow */}
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Rocket className="w-6 h-6 text-yellow-400" />
            How SocialBlast Works
          </CardTitle>
          <CardDescription>Your automated social media posting workflow</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Visual Flow */}
          <div className="relative">
            {/* Desktop Flow - Horizontal */}
            <div className="hidden lg:flex items-start justify-between gap-2">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.number}>
                    <div className="flex-1 text-center">
                      <div className={`w-16 h-16 mx-auto rounded-full ${step.bgColor} flex items-center justify-center mb-3`}>
                        <Icon className={`w-8 h-8 ${step.color}`} />
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <div className={`text-xs font-bold ${step.color} mb-1`}>STEP {step.number}</div>
                        <h3 className="font-semibold text-white mb-1">{step.title}</h3>
                        <p className="text-xs text-gray-400 mb-3">{step.description}</p>
                        <ul className="text-left space-y-1">
                          {step.details.map((detail, i) => (
                            <li key={i} className="text-xs text-gray-300 flex items-start gap-1">
                              <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="flex items-center pt-8">
                        <ArrowRight className="w-6 h-6 text-gray-600" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Mobile Flow - Vertical */}
            <div className="lg:hidden space-y-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.number} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-full ${step.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${step.color}`} />
                      </div>
                      {index < steps.length - 1 && (
                        <div className="w-0.5 h-full bg-gray-700 my-2" />
                      )}
                    </div>
                    <div className="flex-1 bg-gray-800 rounded-lg p-4 mb-2">
                      <div className={`text-xs font-bold ${step.color} mb-1`}>STEP {step.number}</div>
                      <h3 className="font-semibold text-white mb-1">{step.title}</h3>
                      <p className="text-sm text-gray-400 mb-2">{step.description}</p>
                      <ul className="space-y-1">
                        {step.details.map((detail, i) => (
                          <li key={i} className="text-xs text-gray-300 flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Engines */}
      <Card className="bg-gray-900 border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Content Engines Available
          </CardTitle>
          <CardDescription>Automated content generators you can attach to channels</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {engines.map((engine, i) => (
              <div key={i} className="p-3 bg-gray-800 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors">
                <div className="text-2xl mb-1">{engine.emoji}</div>
                <div className="font-medium text-sm text-white">{engine.name}</div>
                <div className="text-xs text-gray-400">{engine.desc}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
        <CardHeader>
          <CardTitle className="text-lg text-green-400 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Pro Tips for Your Team
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-white">✅ Do's</h4>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Start with 2-3 posts/day per channel, increase gradually</li>
                <li>• Use different engines for variety</li>
                <li>• Monitor Control Room daily for errors</li>
                <li>• Test with "Quick Test Post" before going live</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-white">❌ Don'ts</h4>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Don't schedule multiple engines at same time</li>
                <li>• Don't share API keys publicly</li>
                <li>• Don't ignore failed post notifications</li>
                <li>• Don't forget to reconnect expired tokens</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Roles */}
      <Card className="bg-gray-900 border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Team Roles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <Badge className="bg-red-500 mb-2">Admin</Badge>
              <p className="text-sm text-gray-300">Full access: manage users, connect accounts, configure all settings, view analytics</p>
            </div>
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <Badge className="bg-blue-500 mb-2">Editor</Badge>
              <p className="text-sm text-gray-300">Create posts, manage schedules, view all channels, use Quick Test Post</p>
            </div>
            <div className="p-4 bg-gray-500/10 border border-gray-500/30 rounded-lg">
              <Badge className="bg-gray-500 mb-2">Viewer</Badge>
              <p className="text-sm text-gray-300">View-only access: see dashboard, posts, and analytics</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const SetupGuide = () => {
  const [activeTab, setActiveTab] = useState('how-it-works');
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const platforms = [
    {
      id: 'telegram',
      name: 'Telegram',
      icon: MessageCircle,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      difficulty: 'Easy',
      difficultyColor: 'bg-green-500',
      timeEstimate: '5 minutes',
      credentials: ['Bot Token', 'Channel ID'],
      steps: [
        {
          title: 'Create a Telegram Bot',
          description: 'Open Telegram and search for @BotFather',
          substeps: [
            'Start a chat with @BotFather',
            'Send /newbot command',
            'Choose a name for your bot (e.g., "MyChannel Bot")',
            'Choose a username ending in "bot" (e.g., "mychannel_bot")',
            'BotFather will give you an API token - save this!'
          ]
        },
        {
          title: 'Get Your Channel ID',
          description: 'Find your channel\'s unique identifier',
          substeps: [
            'Make your channel public temporarily (if private)',
            'Your channel ID is the @username (e.g., @mychannel)',
            'OR forward a message from your channel to @userinfobot',
            'For public channels, use format: @channelname',
            'For private channels, use the numeric ID (e.g., -1001234567890)'
          ]
        },
        {
          title: 'Add Bot as Admin',
          description: 'Give your bot permission to post',
          substeps: [
            'Go to your Telegram channel',
            'Click on channel name → Administrators',
            'Click "Add Administrator"',
            'Search for your bot username',
            'Enable "Post Messages" permission',
            'Save changes'
          ]
        },
        {
          title: 'Connect in SocialBlast',
          description: 'Add credentials to the platform',
          substeps: [
            'Go to Connected Accounts page',
            'Click Telegram → Connect',
            'Paste your Bot Token',
            'Enter your Channel ID',
            'Click Connect'
          ]
        }
      ],
      tips: [
        'Bot tokens look like: 123456789:ABCdefGHIjklMNOpqrsTUVwxyz',
        'Never share your bot token publicly',
        'You can create multiple bots for different channels'
      ]
    },
    {
      id: 'twitter',
      name: 'Twitter / X',
      icon: Twitter,
      color: 'text-sky-400',
      bgColor: 'bg-sky-500/10',
      difficulty: 'Medium',
      difficultyColor: 'bg-yellow-500',
      timeEstimate: '15-30 minutes',
      credentials: ['API Key', 'API Secret', 'Access Token', 'Access Secret'],
      steps: [
        {
          title: 'Create Developer Account',
          description: 'Sign up for Twitter Developer Portal',
          substeps: [
            'Go to developer.twitter.com',
            'Click "Sign up" (use your Twitter account)',
            'Fill out the application form',
            'Describe your use case (e.g., "Automated posting for business")',
            'Wait for approval (usually instant for basic access)'
          ],
          link: 'https://developer.twitter.com'
        },
        {
          title: 'Create a Project & App',
          description: 'Set up your API access',
          substeps: [
            'Go to Developer Portal Dashboard',
            'Click "Create Project"',
            'Name your project (e.g., "SocialBlast Posting")',
            'Select use case: "Making a bot"',
            'Create an App within the project'
          ]
        },
        {
          title: 'Generate API Keys',
          description: 'Get your authentication credentials',
          substeps: [
            'Go to your App settings',
            'Click "Keys and tokens" tab',
            'Under "Consumer Keys" → click "Regenerate"',
            'Save API Key and API Secret',
            'Under "Authentication Tokens" → Generate Access Token & Secret',
            'Save all 4 values securely'
          ]
        },
        {
          title: 'Set App Permissions',
          description: 'Enable read and write access',
          substeps: [
            'Go to App Settings → "User authentication settings"',
            'Click "Set up"',
            'Select "Read and Write" permissions',
            'App type: "Web App, Automated App or Bot"',
            'Add callback URL: https://social-blast.com/callback',
            'Save changes'
          ]
        },
        {
          title: 'Connect in SocialBlast',
          description: 'Add credentials to the platform',
          substeps: [
            'Go to Connected Accounts page',
            'Click Twitter → Connect',
            'Paste all 4 credentials',
            'Click Connect'
          ]
        }
      ],
      tips: [
        'Free tier allows 1,500 tweets/month',
        'Basic tier ($100/mo) allows 3,000 tweets/month',
        'Keep your API keys secret - never commit to git',
        'Regenerate keys if compromised'
      ]
    },
    {
      id: 'facebook',
      name: 'Facebook Page',
      icon: Facebook,
      color: 'text-blue-500',
      bgColor: 'bg-blue-600/10',
      difficulty: 'Medium',
      difficultyColor: 'bg-yellow-500',
      timeEstimate: '20-30 minutes',
      credentials: ['Page ID', 'Page Access Token'],
      steps: [
        {
          title: 'Create Facebook App',
          description: 'Set up a Meta Developer account',
          substeps: [
            'Go to developers.facebook.com',
            'Click "My Apps" → "Create App"',
            'Select "Business" type',
            'Enter app name and contact email',
            'Create the app'
          ],
          link: 'https://developers.facebook.com'
        },
        {
          title: 'Add Facebook Login Product',
          description: 'Enable page management',
          substeps: [
            'In your app dashboard, click "Add Product"',
            'Find "Facebook Login" and click "Set Up"',
            'Go to Settings → Basic',
            'Add your domain to "App Domains"',
            'Set Privacy Policy URL (can use your website)'
          ]
        },
        {
          title: 'Get Page Access Token',
          description: 'Generate a token for your page',
          substeps: [
            'Go to Graph API Explorer (developers.facebook.com/tools/explorer)',
            'Select your app from dropdown',
            'Click "Generate Access Token"',
            'Select permissions: pages_manage_posts, pages_read_engagement',
            'Select your Page from the dropdown',
            'Copy the generated Page Access Token'
          ],
          link: 'https://developers.facebook.com/tools/explorer'
        },
        {
          title: 'Get Page ID',
          description: 'Find your page\'s unique identifier',
          substeps: [
            'Go to your Facebook Page',
            'Click "About" section',
            'Scroll down to find "Page ID"',
            'Or use Graph API: GET /me?fields=id,name'
          ]
        },
        {
          title: 'Connect in SocialBlast',
          description: 'Add credentials to the platform',
          substeps: [
            'Go to Connected Accounts page',
            'Click Facebook → Connect',
            'Paste Page ID and Page Access Token',
            'Click Connect'
          ]
        }
      ],
      tips: [
        'Page tokens can expire - use long-lived tokens',
        'To get a long-lived token, exchange the short-lived one via API',
        'You need admin access to the Facebook Page',
        'Test in Graph API Explorer before connecting'
      ]
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/10',
      difficulty: 'Hard',
      difficultyColor: 'bg-red-500',
      timeEstimate: '30-45 minutes',
      credentials: ['Instagram Business Account ID', 'Page Access Token'],
      steps: [
        {
          title: 'Convert to Business Account',
          description: 'Instagram API requires a Business or Creator account',
          substeps: [
            'Open Instagram app → Settings',
            'Go to Account → Switch to Professional Account',
            'Select "Business"',
            'Connect to a Facebook Page (required!)',
            'Complete your business profile'
          ]
        },
        {
          title: 'Link to Facebook Page',
          description: 'Connect your Instagram to Facebook',
          substeps: [
            'Go to your Facebook Page',
            'Click Settings → Instagram',
            'Click "Connect Account"',
            'Log in to your Instagram Business account',
            'Authorize the connection'
          ]
        },
        {
          title: 'Set Up Facebook App',
          description: 'Same as Facebook setup above',
          substeps: [
            'Follow Facebook App setup steps (see Facebook tab)',
            'Add Instagram Graph API product to your app',
            'Request instagram_basic and instagram_content_publish permissions'
          ]
        },
        {
          title: 'Get Instagram Business Account ID',
          description: 'Find your IG account ID through Facebook API',
          substeps: [
            'Go to Graph API Explorer',
            'Use query: GET /me/accounts',
            'Find your Page, note the ID',
            'Use query: GET /{page-id}?fields=instagram_business_account',
            'Copy the instagram_business_account.id value'
          ],
          link: 'https://developers.facebook.com/tools/explorer'
        },
        {
          title: 'Connect in SocialBlast',
          description: 'Add credentials to the platform',
          substeps: [
            'Go to Connected Accounts page',
            'Click Instagram → Connect',
            'Paste Instagram Business Account ID',
            'Paste the Page Access Token (same as Facebook)',
            'Click Connect'
          ]
        }
      ],
      tips: [
        'Instagram API only works with Business/Creator accounts',
        'You MUST have a connected Facebook Page',
        'Only image/video posts are supported (no Stories via API)',
        'Carousel posts require special handling'
      ],
      warning: 'Instagram integration is more complex. Make sure your FB Page and IG account are properly linked first.'
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: Music2,
      color: 'text-pink-400',
      bgColor: 'bg-pink-500/10',
      difficulty: 'Hard',
      difficultyColor: 'bg-red-500',
      timeEstimate: '30-60 minutes',
      credentials: ['Client Key', 'Client Secret', 'Access Token'],
      steps: [
        {
          title: 'Create TikTok Developer Account',
          description: 'Register as a TikTok developer',
          substeps: [
            'Go to developers.tiktok.com',
            'Click "Log in" (use your TikTok account)',
            'Complete developer registration',
            'Verify your email'
          ],
          link: 'https://developers.tiktok.com'
        },
        {
          title: 'Create an App',
          description: 'Set up your API access',
          substeps: [
            'Go to "Manage apps" in developer portal',
            'Click "Create app"',
            'Fill in app details',
            'Add product: "Content Posting API"',
            'Submit for review (can take 1-3 days)'
          ]
        },
        {
          title: 'Get Client Credentials',
          description: 'Find your app keys',
          substeps: [
            'Once approved, go to your app',
            'Find "Client Key" and "Client Secret"',
            'Save these securely'
          ]
        },
        {
          title: 'Get User Access Token',
          description: 'Authorize posting to your account',
          substeps: [
            'Implement OAuth flow or use TikTok\'s testing tools',
            'Required scopes: video.publish, video.upload',
            'Complete authorization in browser',
            'Exchange auth code for access token',
            'Save the access_token'
          ]
        },
        {
          title: 'Connect in SocialBlast',
          description: 'Add credentials to the platform',
          substeps: [
            'Go to Connected Accounts page',
            'Click TikTok → Connect',
            'Paste Client Key, Client Secret, and Access Token',
            'Click Connect'
          ]
        }
      ],
      tips: [
        'TikTok ONLY supports VIDEO uploads - no images or text-only posts',
        'Videos must be publicly accessible URLs',
        'Access tokens expire - you may need to refresh periodically',
        'App review can take several days'
      ],
      warning: 'TikTok only accepts VIDEO content. You cannot post images or text-only posts.'
    },
    {
      id: 'discord_webhook',
      name: 'Discord (Webhook)',
      icon: MessageCircle,
      color: 'text-indigo-400',
      bgColor: 'bg-indigo-500/10',
      difficulty: 'Easy',
      difficultyColor: 'bg-green-500',
      timeEstimate: '2 minutes',
      credentials: ['Webhook URL'],
      steps: [
        {
          title: 'Open Discord Server Settings',
          description: 'Navigate to the channel where you want to post',
          substeps: [
            'Open Discord and go to your server',
            'Right-click the channel name (e.g., #announcements)',
            'Click "Edit Channel" (gear icon)',
            'Go to "Integrations" tab'
          ]
        },
        {
          title: 'Create a Webhook',
          description: 'Set up a new webhook for this channel',
          substeps: [
            'Click "Webhooks" section',
            'Click "New Webhook" button',
            'Give it a name (e.g., "SocialBlast Bot")',
            'Optionally upload a custom avatar',
            'Click "Copy Webhook URL"'
          ]
        },
        {
          title: 'Connect in SocialBlast',
          description: 'Add the webhook to your account',
          substeps: [
            'Go to Connected Accounts page',
            'Click Discord (Webhook) → Add Account',
            'Give it a name (e.g., "Fun88 Discord")',
            'Paste the Webhook URL',
            'Optionally set a custom bot username',
            'Click Connect'
          ]
        }
      ],
      tips: [
        'Webhooks are channel-specific - create one per channel',
        'You can customize the bot name and avatar per webhook',
        'Webhook URLs are secret - don\'t share them publicly',
        'No approval or review process needed!'
      ]
    },
    {
      id: 'discord_bot',
      name: 'Discord (Bot)',
      icon: MessageCircle,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-600/10',
      difficulty: 'Medium',
      difficultyColor: 'bg-yellow-500',
      timeEstimate: '15-20 minutes',
      credentials: ['Bot Token', 'Channel ID'],
      steps: [
        {
          title: 'Create Discord Application',
          description: 'Set up a bot in Discord Developer Portal',
          substeps: [
            'Go to discord.com/developers/applications',
            'Click "New Application"',
            'Give it a name (e.g., "SocialBlast Bot")',
            'Click "Create"'
          ],
          link: 'https://discord.com/developers/applications'
        },
        {
          title: 'Create Bot',
          description: 'Add a bot user to your application',
          substeps: [
            'Go to "Bot" section in left menu',
            'Click "Add Bot" → "Yes, do it!"',
            'Under TOKEN, click "Copy" (keep this secret!)',
            'Under "Privileged Gateway Intents"',
            'Enable "MESSAGE CONTENT INTENT"'
          ]
        },
        {
          title: 'Generate Invite URL',
          description: 'Create link to add bot to your server',
          substeps: [
            'Go to "OAuth2" → "URL Generator"',
            'Under SCOPES, select "bot"',
            'Under BOT PERMISSIONS, select:',
            '  • Send Messages',
            '  • Embed Links',
            '  • Attach Files',
            'Copy the generated URL at the bottom'
          ]
        },
        {
          title: 'Add Bot to Server',
          description: 'Invite the bot to your Discord server',
          substeps: [
            'Open the generated URL in browser',
            'Select your server from dropdown',
            'Click "Authorize"',
            'Complete CAPTCHA if prompted'
          ]
        },
        {
          title: 'Get Channel ID',
          description: 'Find the target channel\'s ID',
          substeps: [
            'In Discord, go to User Settings → App Settings → Advanced',
            'Enable "Developer Mode"',
            'Right-click the channel you want to post to',
            'Click "Copy Channel ID"',
            'Channel ID looks like: 1234567890123456789'
          ]
        },
        {
          title: 'Connect in SocialBlast',
          description: 'Add the bot credentials',
          substeps: [
            'Go to Connected Accounts page',
            'Click Discord (Bot) → Add Account',
            'Give it a name (e.g., "Fun88 Bot")',
            'Paste Bot Token',
            'Paste Channel ID',
            'Click Connect'
          ]
        }
      ],
      tips: [
        'One bot can post to multiple channels - just add different Channel IDs',
        'Bot tokens never expire but can be regenerated',
        'Keep your bot token secret - if leaked, regenerate immediately',
        'The bot needs permissions in the channel to post'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">📚 SocialBlast Guide</h1>
          <p className="text-gray-400">Learn how to use SocialBlast and connect your accounts</p>
        </div>

        {/* Main Tabs - How It Works vs Platform Setup */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 bg-gray-800 p-1 mb-6">
            <TabsTrigger value="how-it-works" className="data-[state=active]:bg-green-600 flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              How It Works
            </TabsTrigger>
            <TabsTrigger value="platform-setup" className="data-[state=active]:bg-blue-600 flex items-center gap-2">
              <Key className="w-4 h-4" />
              Platform Setup
            </TabsTrigger>
          </TabsList>

          {/* How It Works Tab */}
          <TabsContent value="how-it-works">
            <HowItWorksSection />
          </TabsContent>

          {/* Platform Setup Tab */}
          <TabsContent value="platform-setup">
            {/* Quick Overview */}
            <Card className="bg-gray-900 border-gray-700 mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Platform Setup Overview</CardTitle>
                <CardDescription>Difficulty and time estimates for each platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-4">
                  {platforms.map(platform => {
                    const Icon = platform.icon;
                    return (
                      <div 
                        key={platform.id}
                        className={`p-4 rounded-lg ${platform.bgColor} border border-gray-700 text-center`}
                      >
                        <Icon className={`w-8 h-8 mx-auto mb-2 ${platform.color}`} />
                        <p className="font-medium text-sm">{platform.name}</p>
                        <Badge className={`${platform.difficultyColor} text-white text-xs mt-1`}>
                          {platform.difficulty}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-1">{platform.timeEstimate}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Platform Tabs */}
            <Tabs defaultValue="telegram" className="w-full">
              <TabsList className="grid grid-cols-4 sm:grid-cols-7 bg-gray-800 p-1">
                {platforms.map(platform => {
                  const Icon = platform.icon;
                  return (
                    <TabsTrigger 
                      key={platform.id}
                      value={platform.id}
                      className="data-[state=active]:bg-gray-700 flex items-center gap-1"
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{platform.name}</span>
                    </TabsTrigger>
                  );
            })}
          </TabsList>

          {platforms.map(platform => {
            const Icon = platform.icon;
            return (
              <TabsContent key={platform.id} value={platform.id} className="mt-4">
                <Card className="bg-gray-900 border-gray-700">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-lg ${platform.bgColor}`}>
                          <Icon className={`w-6 h-6 ${platform.color}`} />
                        </div>
                        <div>
                          <CardTitle>{platform.name} Setup</CardTitle>
                          <CardDescription>
                            {platform.timeEstimate} • {platform.difficulty} difficulty
                          </CardDescription>
                        </div>
                      </div>
                      <Badge className={`${platform.difficultyColor} text-white`}>
                        {platform.difficulty}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Warning if exists */}
                    {platform.warning && (
                      <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-yellow-400">{platform.warning}</p>
                      </div>
                    )}

                    {/* Required Credentials */}
                    <div className="p-4 bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Key className="w-4 h-4 text-blue-400" />
                        <span className="font-medium">Required Credentials</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {platform.credentials.map((cred, i) => (
                          <Badge key={i} variant="outline" className="border-gray-600">
                            {cred}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Steps */}
                    <div className="space-y-4">
                      {platform.steps.map((step, index) => (
                        <div key={index} className="border border-gray-700 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">{step.title}</h3>
                              <p className="text-gray-400 text-sm mb-3">{step.description}</p>
                              
                              <ul className="space-y-2">
                                {step.substeps.map((substep, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm">
                                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                    <span>{substep}</span>
                                  </li>
                                ))}
                              </ul>

                              {step.link && (
                                <a 
                                  href={step.link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 mt-3 text-blue-400 hover:text-blue-300 text-sm"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Open {step.title.includes('Developer') ? 'Developer Portal' : 'Link'}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Tips */}
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-green-400" />
                        <span className="font-medium text-green-400">Pro Tips</span>
                      </div>
                      <ul className="space-y-2">
                        {platform.tips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                            <span className="text-green-400">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
            </Tabs>

            {/* Need Help */}
            <Card className="bg-gray-900 border-gray-700 mt-6">
              <CardContent className="p-6 text-center">
                <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <h3 className="font-medium text-lg mb-2">Need Help?</h3>
                <p className="text-gray-400 text-sm">
                  Having trouble connecting an account? Contact your admin for assistance.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SetupGuide;
